import { Fragment } from 'react';
import { LandingPageDynamicImportError } from '../../utils/errors';
import withErrorBoundary from './components/ErrorBoundary';

/**
 * Return true if parentNode has only one element, and that node is a string.
 */
const isTextNode = (parentNode) => {
  const [child] = parentNode;
  return parentNode.length === 1 && typeof child === 'string';
};

/**
 * Walks a tree of components in the format output by parseComponentTree(), and
 * for each newly-encountered component, creates an import callback suitable for
 * use with Next.js’ dynamic() loader. For an example of the structure consumed
 * here, see app/areas/landingPages/factories.test.js.
 *
 * @param {object} tree
 */
export function importComponentTree(tree) {
  const components = {};
  const parseTree = (parentNode) => {
    if (isTextNode(parentNode)) return;

    parentNode.forEach(({ componentName, children }) => {
      if (typeof components[componentName] === 'undefined') {
        try {
          components[
            componentName
          ] = import(/* webpackChunkName: "dynamicTree" */ `./dynamic/${componentName}`);
        } catch (error) {
          throw new LandingPageDynamicImportError(error);
        }
      }

      if (children && children.length) {
        parseTree(children);
      }
    });
  };

  parseTree(tree);

  return components;
}

/**
 * Walks a tree of components, rendering all children.
 *
 * @param {object} tree
 * @param {object} components
 */
export function renderComponentTree(tree, components) {
  const renderTree = (parentNode) => {
    if (isTextNode(parentNode)) {
      const [child] = parentNode;
      return withErrorBoundary(Fragment, {}, child);
    }

    return parentNode.map(({ componentName, key, props, children }) => {
      const propsWithKey = { key, ...props };
      const Component = components[componentName];

      if (children && children.length) {
        return withErrorBoundary(Component, propsWithKey, renderTree(children));
      }

      return withErrorBoundary(Component, propsWithKey);
    });
  };

  return renderTree(tree);
}
